import React, { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";

interface ArticleActionsMenuProps {
  articleId: string | number;
  articleType: "kfs" | "bur";
  articleTitle: string;
  articleUrl: string;
  isAdmin: boolean;
  onReprocess: (
    articleId: string | number,
    type: "kfs" | "bur"
  ) => Promise<void>;
  onReport: (articleId: string | number, type: "kfs" | "bur") => void;
  onClose?: (articleId: string | number, type: "kfs" | "bur") => Promise<void>;
  onEditFlags?: (articleId: string | number, type: "kfs" | "bur") => void;
}

const ArticleActionsMenu: React.FC<ArticleActionsMenuProps> = ({
  articleId,
  articleType,
  articleTitle,
  articleUrl,
  isAdmin,
  onReprocess,
  onReport,
  onClose,
  onEditFlags,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isReprocessing, setIsReprocessing] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [menuPosition, setMenuPosition] = useState<{
    top: number;
    left: number;
  } | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const isAnyActionLoading = isReprocessing || isClosing;

  // Calculate menu position when opening
  const calculateMenuPosition = () => {
    if (!buttonRef.current) return;

    const rect = buttonRef.current.getBoundingClientRect();
    const menuWidth = 192; // w-48 = 192px
    const menuHeight = 60; // estimated height

    let left = rect.right - menuWidth;
    let top = rect.bottom + 4; // mt-1 = 4px

    // Check if menu would go off the left edge
    if (left < 10) {
      left = rect.left;
    }

    // Check if menu would go off the bottom edge
    if (top + menuHeight > window.innerHeight - 10) {
      top = rect.top - menuHeight - 4;
    }

    // Ensure menu doesn't go off-screen
    left = Math.max(10, Math.min(left, window.innerWidth - menuWidth - 10));

    setMenuPosition({ top, left });
  };

  const handleToggleMenu = (e: React.MouseEvent) => {
    e.stopPropagation();

    if (!isOpen) {
      calculateMenuPosition();
    }
    setIsOpen(!isOpen);
  };

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // Check if click is on the button
      if (
        buttonRef.current &&
        buttonRef.current.contains(event.target as Node)
      ) {
        return;
      }

      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [isOpen]);

  const handleReprocess = async () => {
    setIsReprocessing(true);
    try {
      await onReprocess(articleId, articleType);
      setIsOpen(false);
    } catch (error) {
      // Error handling done in parent
    } finally {
      setIsReprocessing(false);
    }
  };

  const handleClose = async () => {
    if (!onClose) return;
    setIsClosing(true);
    try {
      await onClose(articleId, articleType);
      setIsOpen(false);
    } catch (error) {
      // Error handling done in parent
    } finally {
      setIsClosing(false);
    }
  };

  const handleReport = () => {
    setIsOpen(false);
    onReport(articleId, articleType);
  };

  const handleEditFlags = () => {
    setIsOpen(false);
    onEditFlags?.(articleId, articleType);
  };

  return (
    <div className="relative" ref={menuRef}>
      <button
        ref={buttonRef}
        onClick={handleToggleMenu}
        className="p-1 rounded hover:bg-neutral-100 transition-colors"
        title="Akcje"
        aria-label="Menu akcji artykułu"
        disabled={isAnyActionLoading}
      >
        <svg
          className="w-5 h-5 text-neutral-600"
          fill="currentColor"
          viewBox="0 0 20 20"
        >
          <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
        </svg>
      </button>

      {isOpen &&
        menuPosition &&
        createPortal(
          <div
            ref={menuRef}
            className="fixed w-48 bg-white rounded-lg shadow-lg border border-neutral-200 z-50 py-1"
            style={{
              top: `${menuPosition.top}px`,
              left: `${menuPosition.left}px`,
            }}
          >
            <button
              onClick={handleReport}
              className="w-full text-left px-4 py-2 text-sm text-neutral-700 hover:bg-neutral-50 flex items-center"
            >
              <svg
                className="w-4 h-4 mr-2 text-red-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
              Zgłoś
            </button>

            {isAdmin && onClose && (
              <button
                onClick={handleClose}
                disabled={isAnyActionLoading}
                className="w-full text-left px-4 py-2 text-sm text-neutral-700 hover:bg-neutral-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              >
                {isClosing ? (
                  <>
                    <svg
                      className="animate-spin h-4 w-4 mr-2 text-primary-600"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                    Kończę nabór...
                  </>
                ) : (
                  <>
                    <svg
                      className="w-4 h-4 mr-2 text-neutral-500"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 6l12 12M6 18L18 6"
                      />
                    </svg>
                    Zakończ nabór
                  </>
                )}
              </button>
            )}

            {isAdmin && onEditFlags && (
              <button
                onClick={handleEditFlags}
                disabled={isAnyActionLoading}
                className="w-full text-left px-4 py-2 text-sm text-neutral-700 hover:bg-neutral-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                aria-label={`Edytuj artykuł ${articleTitle}`}
              >
                <svg
                  className="w-4 h-4 mr-2 text-neutral-500"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                  />
                </svg>
                Edytuj
              </button>
            )}

            {isAdmin && (
              <button
                onClick={handleReprocess}
                disabled={isAnyActionLoading}
                className="w-full text-left px-4 py-2 text-sm text-neutral-700 hover:bg-neutral-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              >
                {isReprocessing ? (
                  <>
                    <svg
                      className="animate-spin h-4 w-4 mr-2 text-primary-600"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                    Przetwarzanie...
                  </>
                ) : (
                  <>
                    <svg
                      className="w-4 h-4 mr-2 text-neutral-500"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                      />
                    </svg>
                    Przetwórz ponownie
                  </>
                )}
              </button>
            )}
          </div>,
          document.body
        )}
    </div>
  );
};

export default ArticleActionsMenu;
